<template>
  <section class="proposals-list">
    <proposals-list-filters
      class="proposals-list-filters"
      :scrollable="scrollable"
      :per-page="perPage"
    />

    <ul class="proposals-list-items">
      <li
        v-for="proposal in list"
        :key="proposal.id"
        class="proposals-list-item"
      >
        <router-link
          :to="link(proposal)"
          class="proposals-list-link"
        >
          <typography-heading
            hierarchy="h3"
            :icon="sdk.mobile ? null : 'chevron-right'"
          >
            {{ proposal.customer.name | namelize }}

            <template v-slot:subtitle>
              #{{ proposal.id }} - cadastrada dia {{ proposal.created_at | createdAt }}
              <context-tag
                v-if="tagged(proposal)"
                color="external"
                class="proposals-list-online"
                :icon="['fas', 'right-to-bracket']"
              >
                Via {{ tagged(proposal) }}
              </context-tag>
            </template>
          </typography-heading>

          <information-line
            :label="vehicle(proposal)"
            :copiable="false"
            valueless
            class="proposals-list-line"
          />

          <information-line
            class="proposals-list-seller"
            :label="values(proposal)"
          >
            <context-tag
              :color="integrated(proposal.state)"
              class="proposals-list-tag"
            >
              {{ proposal.state | status }}
            </context-tag>
          </information-line>

          <information-line
            :label="seller(proposal)"
            :value="store(proposal)"
            :copiable="false"
            class="proposals-list-seller"
          />
        </router-link>

        <span class="proposals-list-separator" />
      </li>

      <context-state
        :state="currentState"
        tag="li"
      >
        <template v-slot:loading>
          Carregando propostas...
        </template>

        <template v-slot:notfound>
          Nenhuma proposta encontrada :(
        </template>

        <template v-slot:empty>
          Sem mais propostas
        </template>
      </context-state>
    </ul>
  </section>
</template>

<script>
  import { mapState } from 'vuex';
  import mobile from '@sdk/helpers/interface/mobile';
  import origin from '@sdk/helpers/interface/origin';

  import namelize from '@sdk/helpers/formatters/namelize';
  import { installments } from '@sdk/helpers/misc/installments';
  import monetize from '@sdk/helpers/formatters/monetize';

  import InformationLine from '@cordel/components/Information/Line.vue';
  import TypographyHeading from '@cordel/components/Typography/Heading.vue';

  import ContextState from '@cordel/components/Context/State.vue';
  import ContextTag from '@cordel/components/Context/Tag.vue';

  import ProposalsListFilter from '@sdk/views/Proposals/List/Filter.vue';
  import ProposalsListFilters from '@sdk/views/Proposals/List/Filters.vue';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import sort from '@/_CRUDL/helpers/data/sort';

  // eslint-disable-next-line import/no-extraneous-dependencies
  import { INITIAL_PROPOSAL_FILTERS as INITIAL } from '@/store/modules/proposals';

  export default {
    name: 'ProposalsList',
    components: {
      ContextState,
      ContextTag,

      InformationLine,
      TypographyHeading,
      ProposalsListFilters,
    },
    mixins: [
      mobile,
    ],
    props: {
      perPage: {
        type: Number,
        default: INITIAL.per_page,
      },
      scrollable: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        nav: {
          action: ProposalsListFilter,
        },
      };
    },
    computed: {
      ...mapState([
        'proposals',
      ]),

      filters() {
        return this.proposals.list.filters;
      },
      list() {
        return sort(this.proposals.list.items, this.filters.sort);
      },
      loading() {
        return this.proposals.list.loading;
      },
      currentState() {
        if (this.loading) {
          return 'loading';
        }

        if (this.list.length === 0) {
          return 'notfound';
        }

        if (this.scrollable) {
          return 'empty';
        }

        return null;
      },
    },
    beforeMount() {
      this.$store.dispatch('proposals/clear', { action: 'list' });
    },

    methods: {
      vehicle(proposal) {
        const brand = proposal.vehicle_model && proposal.vehicle_model.brand;
        const name = proposal.vehicle_model && proposal.vehicle_model.name;

        return `${brand || 'MARCA'} ${name || 'Veículo desconhecido'}`;
      },
      seller(proposal) {
        if (proposal.seller) {
          return namelize(proposal.seller.name, 2);
        }

        return 'Vendedor desativado';
      },
      store(proposal) {
        if (proposal.store) {
          return proposal.store.name;
        }

        return 'Loja desativada';
      },
      values(proposal) {
        if (!proposal || !proposal.proposal_attempt
        || !proposal.proposal_attempt.input_financing_in_cents) {
          return 'Não disponível';
        }
        const [installment, balloon] = installments(proposal.proposal_attempt);
        return `${monetize(proposal.proposal_attempt.input_financing_in_cents)} + ${installment.value} ${balloon ? '+ balões' : ''}`;
      },

      integrated(state) {
        switch (state) {
          case 'checagem':
          case 'análise':
            return 'status-01';
          case 'pendente':
          case 'pré_aprovada':
            return 'status-02';
          case 'recusada':
          case 'desistencia':
          case 'restrição':
          case 'expirada':
            return 'status-03';
          case 'aprovada':
          case 'faturada':
            return 'status-04';
          default:
            return 'status-03';
        }
      },
      link(proposal) {
        return {
          name: 'proposals-read',
          params: {
            id: proposal.id,
          },
        };
      },
      tagged(proposal) {
        if (proposal) {
          return origin(proposal) || origin(proposal.proposal_attempt);
        }

        return null;
      },
    },
  };
</script>

<style lang="scss">
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-list-filters {
    .cordel-section-item {
      margin-top: var(--cordel-spacing-s);

      &:first-child {
        margin-top: 0;
      }
    }
  }
</style>

<style lang="scss" scoped>
  @import '@cordel/assets/stylesheets/mixins/reset.scss';
  @import '@cordel/assets/stylesheets/variables/breakpoints.scss';

  .proposals-list-items {
    list-style: none;
    margin-top: var(--cordel-spacing-xl);
    overflow: hidden;
  }

  .proposals-list-separator {
    background-color: var(--cordel-color-neutral-dark);
    display: block;
    height: var(--cordel-border-width-soft);
    margin: 0 var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      margin: 0 var(--product--pwa--horizontal);
    }
  }

  .proposals-list-link {
    display: block;
    padding: var(--cordel-spacing-xl) var(--product--pwa--horizontal);

    @media all and (max-width: $cordel-breakpoint-mobile) {
      padding: var(--cordel-spacing-xl) var(--product--pwa--horizontal);
    }

    &:hover {
      background: var(--cordel-color-neutral-lighter);
    }

    &:focus {
      box-shadow: inset 0 0 0 var(--cordel-border-width-thin) var(--cordel-color-highlight-primary);
      outline: none;
    }
  }

  .proposals-list-line {
    margin-top: var(--cordel-spacing-xxl);
  }

  .proposals-list-seller {
    margin-top: var(--cordel-spacing-mini);
  }

  .proposals-list-tag {
    margin-left: var(--cordel-spacing-xxxs);
    flex-shrink: 0;
    text-transform: capitalize;
  }

  .proposals-list-online {
    flex-shrink: 0;
    margin-top: var(--cordel-spacing-nano);
    margin-left: var(--cordel-spacing-nano);
  }
</style>
